import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import api from "@/lib/api";
import {
  Bird, Plus, ArrowRight, Loader2, Users, Zap, Copy, Check,
  LayoutDashboard, Truck, HardHat, Signal, ChevronRight, LogOut, RefreshCw,
  Wrench, ArrowRightLeft, Eye, Sparkles, Lock, ArrowLeft, X, PlusCircle,
  Fuel, Droplets, Layers, Package, ClipboardCheck, Trash2, Bookmark, BookmarkCheck,
  QrCode, Share2, Download
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import HUB_TYPES, { getAvailableTypes, ROLE_CONFIGS } from "@/lib/hubTypes";

const ROLE_CONFIG = {
  planner: { icon: LayoutDashboard, color: "bg-lime-500", textColor: "text-slate-950", label: "Planner", mandatory: true },
  driver: { icon: Truck, color: "bg-red-600", textColor: "text-white", label: "Driver", mandatory: true },
  machinist: { icon: HardHat, color: "bg-amber-500", textColor: "text-slate-950", label: "Machinist", mandatory: true },
  service: { icon: Wrench, color: "bg-purple-600", textColor: "text-white", label: "Service", mandatory: false },
  transport: { icon: ArrowRightLeft, color: "bg-blue-600", textColor: "text-white", label: "Transport", mandatory: false },
  viewer: { icon: Eye, color: "bg-slate-600", textColor: "text-white", label: "Viewer", mandatory: false },
};

export default function HubPortal() {
  const { auth, setAuth, setSession } = useAppContext();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const joinInvite = searchParams.get("invite") || "";

  const [view, setView] = useState(joinInvite ? "join-preview" : "list");
  const [myHubs, setMyHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinInfo, setJoinInfo] = useState(null);
  const [joinDisplayName, setJoinDisplayName] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);

  // Create hub state
  const [hubName, setHubName] = useState("");
  const [hubDesc, setHubDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedAssetType, setSelectedAssetType] = useState("");
  const [selectedRoleConfig, setSelectedRoleConfig] = useState("full");
  const [customItems, setCustomItems] = useState({ a: "", b: "", c: "" });
  const [customUnits, setCustomUnits] = useState({ a: "", b: "", c: "" });
  const [itemBEnabled, setItemBEnabled] = useState(true);
  const [itemCEnabled, setItemCEnabled] = useState(false);
  const [customActions, setCustomActions] = useState([]);

  // Template state
  const [templates, setTemplates] = useState([]);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Hub lobby state
  const [activeHub, setActiveHub] = useState(null);
  const [hubStatus, setHubStatus] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviteRole, setInviteRole] = useState("driver");
  const [inviteName, setInviteName] = useState("");
  const [copied, setCopied] = useState("");
  const [expandedQR, setExpandedQR] = useState(null);

  // Demo hub state
  const [showDemo, setShowDemo] = useState(false);
  const [demoRole, setDemoRole] = useState("planner");
  const [seedingDemo, setSeedingDemo] = useState(false);

  const jwt = auth?.jwt;

  const loadMyHubs = useCallback(async () => {
    if (!jwt) return;
    setLoading(true);
    try {
      const hubs = await api.getMyHubs(jwt);
      setMyHubs(hubs);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [jwt]);

  const loadTemplates = useCallback(async () => {
    if (!jwt) return;
    try {
      const tpls = await api.getTemplates(jwt);
      setTemplates(tpls);
    } catch (e) { console.error(e); }
  }, [jwt]);

  useEffect(() => {
    if (joinInvite && jwt) {
      loadInviteInfo(joinInvite);
    } else {
      loadMyHubs();
      loadTemplates();
    }
  }, [jwt, joinInvite, loadMyHubs, loadTemplates]);

  const loadInviteInfo = async (token) => {
    try {
      const info = await api.getInviteInfo(token);
      setJoinInfo(info);
      setJoinDisplayName(auth?.user?.email?.split("@")[0] || "");
      setLoading(false);
    } catch (e) {
      console.error("Invalid invite:", e);
      setView("list");
      loadMyHubs();
      loadTemplates();
    }
  };

  const handleConfirmJoin = async () => {
    if (!joinInvite || !jwt) return;
    setJoinLoading(true);
    try {
      await api.joinHub(jwt, { invite_token: joinInvite, display_name: joinDisplayName || auth?.user?.email?.split("@")[0] || "" });
      setView("list");
      await loadMyHubs();
      nav("/portal");
    } catch (e) {
      console.error(e);
      setView("list");
      await loadMyHubs();
    }
    setJoinLoading(false);
  };

  const handleCreateHub = async (e) => {
    e.preventDefault();
    if (!hubName.trim() || !selectedAssetType) return;
    setCreating(true);
    try {
      const config = HUB_TYPES[selectedAssetType] || HUB_TYPES.refueling;
      const roleConf = ROLE_CONFIGS.find(r => r.id === selectedRoleConfig) || ROLE_CONFIGS[0];
      const result = await api.createHubAuth(jwt, {
        name: hubName,
        description: hubDesc,
        asset_type: selectedAssetType,
        asset_config: {
          item_a_label: customItems.a || config.item_a.label,
          item_a_unit: customUnits.a || config.item_a.unit,
          item_b_label: customItems.b || config.item_b.label,
          item_b_unit: customUnits.b || config.item_b.unit,
          item_b_enabled: itemBEnabled && config.item_b.enabled !== false,
          item_c_label: customItems.c || config.item_c?.label || "",
          item_c_unit: customUnits.c || config.item_c?.unit || "",
          item_c_enabled: itemCEnabled,
          level_label: config.level.label,
          request_label: config.request_label,
          delivery_label: config.delivery_label,
          request_type_primary: config.request_type_primary,
          role_config: selectedRoleConfig,
          min_occupancy: roleConf.min_occupancy,
          actions: config.is_task_based ? customActions : [],
        },
      });
      setActiveHub(result.hub);
      await loadHubStatus(result.hub.id);
      // Save as template if requested
      if (saveAsTemplate && templateName.trim()) {
        try {
          await api.saveTemplate(jwt, {
            name: templateName.trim(),
            asset_type: selectedAssetType,
            asset_config: result.hub.asset_config,
          });
          await loadTemplates();
        } catch (e) { console.error("Failed to save template:", e); }
      }
      setView("lobby");
      setHubName(""); setHubDesc(""); setSelectedAssetType(""); setWizardStep(1);
      setCustomItems({ a: "", b: "", c: "" }); setCustomUnits({ a: "", b: "", c: "" });
      setItemBEnabled(true); setItemCEnabled(false); setCustomActions([]);
      setSelectedRoleConfig("full");
      setSaveAsTemplate(false); setTemplateName("");
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  const loadHubStatus = async (hubId) => {
    try {
      const status = await api.getHubStatus(hubId);
      setHubStatus(status);
      const invs = await api.getInvites(jwt, hubId);
      setInvites(invs);
    } catch (e) { console.error(e); }
  };

  const applyTemplate = (tpl) => {
    const ac = tpl.asset_config || {};
    setSelectedAssetType(tpl.asset_type);
    const cfg = HUB_TYPES[tpl.asset_type] || HUB_TYPES.refueling;
    setCustomItems({ a: ac.item_a_label || cfg.item_a.label, b: ac.item_b_label || cfg.item_b.label, c: ac.item_c_label || "" });
    setCustomUnits({ a: ac.item_a_unit || cfg.item_a.unit, b: ac.item_b_unit || cfg.item_b.unit, c: ac.item_c_unit || "" });
    setItemBEnabled(ac.item_b_enabled !== undefined ? ac.item_b_enabled : cfg.item_b.enabled !== false);
    setItemCEnabled(ac.item_c_enabled || false);
    setSelectedRoleConfig(ac.role_config || "full");
    setCustomActions(ac.actions || []);
    if (cfg.is_task_based && (!ac.actions || ac.actions.length === 0)) setCustomActions([""]);
    setWizardStep(2);
  };

  const handleDeleteTemplate = async (tplId) => {
    try {
      await api.deleteTemplate(jwt, tplId);
      setTemplates(prev => prev.filter(t => t.id !== tplId));
    } catch (e) { console.error(e); }
  };

  const handleDeleteHub = async (hubId) => {
    try {
      await api.deleteHub(jwt, hubId);
      setDeleteConfirmId(null);
      await loadMyHubs();
    } catch (e) { console.error(e); }
  };

  const handleEnterHub = (membership) => {
    const hubId = membership.hub_id;
    const role = membership.role;
    const sess = {
      hubId,
      hubName: membership.hub_name || hubId,
      role,
      lang: "en",
      driverName: membership.display_name || role,
      userName: membership.display_name || auth?.user?.email?.split("@")[0] || role,
      asset_type: membership.asset_type || "refueling",
      asset_config: membership.asset_config || {},
    };
    setSession(sess);
    if (role === "planner") nav("/planner");
    else if (role === "driver") nav(`/driver/${membership.display_name || ""}`);
    else if (role === "machinist") nav("/machinist");
    else if (role === "service") nav("/service");
    else if (role === "transport") nav("/transport");
    else nav("/viewer");
  };

  const handleOpenLobby = async (membership) => {
    const hubId = membership.hub_id;
    const hub = { id: hubId, name: membership.hub_name, status: membership.hub_status };
    setActiveHub(hub);
    await loadHubStatus(hubId);
    setView("lobby");
  };

  const handleCreateInvite = async () => {
    if (!activeHub) return;
    try {
      await api.createInvite(jwt, activeHub.id, { role: inviteRole, display_name: inviteName });
      await loadHubStatus(activeHub.id);
      setInviteName("");
    } catch (e) { console.error(e); }
  };

  const copyInviteLink = async (token) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/auth?invite=${token}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(token);
    } catch {
      const el = document.getElementById(`invite-link-${token}`);
      if (el) { el.select(); document.execCommand("copy"); setCopied(token); }
    }
    setTimeout(() => setCopied(""), 3000);
  };

  const qrToBlob = (token) => {
    return new Promise((resolve) => {
      const svg = document.getElementById(`qr-svg-${token}`);
      if (!svg) return resolve(null);
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement("canvas");
      canvas.width = 512; canvas.height = 600;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 512, 600);
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 56, 24, 400, 400);
        ctx.fillStyle = "#0f172a";
        ctx.font = "bold 16px Inter, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(activeHub?.name || "Pigeon Supply Hub", 256, 460);
        ctx.fillStyle = "#64748b";
        ctx.font = "13px Inter, sans-serif";
        const inv = invites.find(i => i.token === token);
        ctx.fillText(`Join as ${ROLE_CONFIG[inv?.role]?.label || "Member"}`, 256, 484);
        ctx.fillStyle = "#84cc16";
        ctx.fillRect(156, 500, 200, 3);
        ctx.fillStyle = "#94a3b8";
        ctx.font = "11px Inter, sans-serif";
        ctx.fillText("Pigeon Supply", 256, 530);
        canvas.toBlob(resolve, "image/png");
      };
      img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    });
  };

  const shareInvite = async (token) => {
    const link = `${window.location.origin}/auth?invite=${token}`;
    const inv = invites.find(i => i.token === token);
    const roleName = ROLE_CONFIG[inv?.role]?.label || "Member";
    const hubName = activeHub?.name || "Hub";
    const text = `Join ${hubName} as ${roleName} on Pigeon Supply`;

    if (navigator.share) {
      try {
        const blob = await qrToBlob(token);
        const shareData = { title: text, text: `${text}\n${link}`, url: link };
        if (blob && navigator.canShare?.({ files: [new File([blob], "invite-qr.png", { type: "image/png" })] })) {
          shareData.files = [new File([blob], `pigeon-invite-${roleName.toLowerCase()}.png`, { type: "image/png" })];
        }
        await navigator.share(shareData);
      } catch (e) {
        if (e.name !== "AbortError") copyInviteLink(token);
      }
    } else {
      copyInviteLink(token);
    }
  };

  const downloadQR = async (token) => {
    const blob = await qrToBlob(token);
    if (!blob) return;
    const inv = invites.find(i => i.token === token);
    const roleName = ROLE_CONFIG[inv?.role]?.label || "member";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pigeon-invite-${roleName.toLowerCase()}-${token}.png`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleEnterDemoHub = async () => {
    setSeedingDemo(true);
    try {
      await api.seedDemoHub();
      const sess = {
        hubId: "DEMO-TRAINING-HUB",
        hubName: "Demo & Training Hub",
        role: demoRole,
        lang: "en",
        driverName: `Demo ${ROLE_CONFIG[demoRole]?.label || "User"}`,
        userName: `Demo ${ROLE_CONFIG[demoRole]?.label || "User"}`,
      };
      setSession(sess);
      if (demoRole === "planner") nav("/planner");
      else if (demoRole === "driver") nav("/driver/Demo Driver");
      else if (demoRole === "machinist") nav("/machinist");
      else if (demoRole === "service") nav("/service");
      else if (demoRole === "transport") nav("/transport");
      else nav("/viewer");
    } catch (e) { console.error(e); }
    setSeedingDemo(false);
  };

  const handleLogout = () => {
    setAuth(null);
    setSession(null);
    localStorage.removeItem("pigeon-auth");
    localStorage.removeItem("pigeon-session");
    nav("/auth");
  };

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col relative overflow-hidden" data-testid="hub-portal">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(101,163,13,0.04),transparent_50%)]" />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 py-5 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center shadow-md">
            <Bird className="w-5 h-5 text-slate-950" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">Pigeon Supply</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{auth?.user?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {view === "lobby" && (
            <button onClick={() => { setView("list"); loadMyHubs(); }} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-xs font-bold border border-slate-200" data-testid="back-to-hubs-btn">
              Back
            </button>
          )}
          <button onClick={handleLogout} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-xs font-bold border border-slate-200 flex items-center gap-2" data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center p-6">
        {/* HUB LIST */}
        {view === "list" && (
          <div className="w-full max-w-xl" data-testid="hub-list-view">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase text-slate-900 mb-2">{t("my_hubs")}</h2>
              <p className="text-sm text-slate-400 font-medium">Your logistics hubs</p>
            </div>

            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-lime-500 animate-spin" /></div>
            ) : (
              <>
                {myHubs.length > 0 && (
                  <div className="grid gap-3 mb-6">
                    {myHubs.map((m) => {
                      const rc = ROLE_CONFIG[m.role] || ROLE_CONFIG.viewer;
                      const Icon = rc.icon;
                      const isDeployed = m.hub_status === "DEPLOYED";
                      return (
                        <div key={m.id} className="relative group" data-testid={`hub-card-${m.hub_id}`}>
                          {deleteConfirmId === m.hub_id ? (
                            <div className="p-5 bg-red-50 border border-red-300 rounded-xl animate-fadeIn" data-testid={`delete-confirm-${m.hub_id}`}>
                              <p className="text-sm font-bold text-red-600 mb-1">Delete "{m.hub_name || m.hub_id}"?</p>
                              <p className="text-[10px] text-red-400 mb-3">All sites, machines, requests, and data will be permanently removed.</p>
                              <div className="flex gap-2">
                                <button data-testid={`confirm-delete-${m.hub_id}`} onClick={() => handleDeleteHub(m.hub_id)}
                                  className="flex-1 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-bold uppercase active:scale-95 transition-all">
                                  Delete Forever
                                </button>
                                <button data-testid={`cancel-delete-${m.hub_id}`} onClick={() => setDeleteConfirmId(null)}
                                  className="flex-1 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 rounded-lg text-xs font-bold uppercase">
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <button
                                data-testid={`hub-entry-${m.hub_id}`}
                                onClick={() => isDeployed ? handleEnterHub(m) : handleOpenLobby(m)}
                                className="flex-1 p-5 bg-slate-50 border border-slate-200 rounded-xl hover:border-lime-400 transition-all text-left flex items-center justify-between active:scale-[0.98]"
                              >
                                <div className="flex items-center gap-4">
                                  <div className={`w-12 h-12 ${rc.color} rounded-xl flex items-center justify-center ${rc.textColor}`}>
                                    <Icon className="w-6 h-6" strokeWidth={2} />
                                  </div>
                                  <div>
                                    <h3 className="text-base font-bold text-slate-900 tracking-tight">{m.hub_name || m.hub_id}</h3>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{rc.label}</span>
                                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDeployed ? "bg-lime-100 text-lime-600" : "bg-amber-100 text-amber-400"}`}>
                                        {isDeployed ? "DEPLOYED" : "WAITING"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-lime-500 transition-all" />
                              </button>
                              <button data-testid={`delete-hub-${m.hub_id}`} onClick={() => setDeleteConfirmId(m.hub_id)}
                                className="p-3 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all rounded-xl hover:bg-red-50">
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <button
                  data-testid="create-hub-btn"
                  onClick={() => setView("create")}
                  className="w-full p-6 border-2 border-dashed border-slate-200 hover:border-lime-400 rounded-xl flex items-center justify-center gap-3 text-slate-400 hover:text-lime-600 transition-all group"
                >
                  <Plus className="w-6 h-6 group-hover:rotate-90 transition-transform" />
                  <span className="text-sm font-bold uppercase tracking-wider">{t("create_hub")}</span>
                </button>

                {/* Demo Hub Section */}
                <button
                  data-testid="try-demo-hub-btn"
                  onClick={() => setShowDemo(true)}
                  className="w-full mt-3 p-5 bg-amber-50 border border-amber-200 hover:border-amber-400 rounded-xl flex items-center justify-center gap-3 text-amber-400 hover:text-amber-300 transition-all group"
                >
                  <Sparkles className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-bold uppercase tracking-wider">Try Demo & Training Hub</span>
                </button>

                {/* Demo Role Picker Modal */}
                {showDemo && (
                  <div className="mt-4 p-5 bg-slate-50 border border-amber-300 rounded-xl animate-fadeIn" data-testid="demo-role-picker">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 text-center">Choose a role to explore</h3>
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {Object.entries(ROLE_CONFIG).map(([role, rc]) => {
                        const Icon = rc.icon;
                        return (
                          <button
                            key={role}
                            data-testid={`demo-role-${role}`}
                            onClick={() => setDemoRole(role)}
                            className={`p-3 rounded-xl text-center transition-all border ${
                              demoRole === role
                                ? "bg-amber-50 border-amber-400 shadow-sm"
                                : "bg-slate-50/80 border-slate-200 hover:border-slate-200"
                            }`}
                          >
                            <div className={`w-8 h-8 ${demoRole === role ? rc.color : "bg-slate-100"} rounded-xl flex items-center justify-center mx-auto mb-2 ${demoRole === role ? rc.textColor : "text-slate-400"}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <p className={`text-[10px] font-bold uppercase tracking-tight ${demoRole === role ? "text-amber-400" : "text-slate-400"}`}>{rc.label}</p>
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setShowDemo(false)} className="flex-1 py-3 bg-slate-100 text-slate-400 rounded-xl text-xs font-bold">Cancel</button>
                      <button
                        data-testid="enter-demo-hub-btn"
                        onClick={handleEnterDemoHub}
                        disabled={seedingDemo}
                        className="flex-[2] py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 text-slate-950 rounded-xl text-xs font-black uppercase flex items-center justify-center gap-2 shadow-md"
                      >
                        {seedingDemo ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Enter as {ROLE_CONFIG[demoRole]?.label}</>}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* CREATE HUB */}
        {view === "create" && (
          <div className="w-full max-w-lg" data-testid="create-hub-view">
            {/* Wizard Step Indicator */}
            <div className="flex items-center justify-center gap-3 mb-10">
              {[1, 2, 3].map(s => (
                <div key={s} className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black transition-all ${
                    wizardStep >= s ? "bg-lime-500 text-slate-950 shadow-sm" : "bg-slate-100 text-slate-400"
                  }`}>{s}</div>
                  {s < 3 && <div className={`w-10 h-0.5 rounded-full ${wizardStep > s ? "bg-lime-500" : "bg-slate-200"}`} />}
                </div>
              ))}
            </div>

            {/* Step 1: Choose Hub Type */}
            {wizardStep === 1 && (
              <div className="animate-fadeInUp">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-black tracking-tighter uppercase text-slate-900 mb-2">Choose Hub Focus</h2>
                  <p className="text-sm text-slate-400 font-medium">What will this hub manage?</p>
                </div>

                {/* Saved Templates */}
                {templates.length > 0 && (
                  <div className="mb-6" data-testid="templates-section">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5"><BookmarkCheck className="w-3.5 h-3.5 text-amber-500" /> Saved Templates</p>
                    <div className="grid grid-cols-2 gap-2">
                      {templates.map(tpl => {
                        const tplCfg = HUB_TYPES[tpl.asset_type] || HUB_TYPES.refueling;
                        const TplIcon = tplCfg.icon;
                        return (
                          <div key={tpl.id} role="button" tabIndex={0} data-testid={`template-${tpl.id}`} onClick={() => applyTemplate(tpl)}
                            className="p-3 bg-amber-50 border border-amber-200 hover:border-amber-400 rounded-xl text-left flex items-center gap-3 transition-all group cursor-pointer">
                            <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600 shrink-0">
                              <TplIcon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-xs font-bold text-slate-900 block truncate">{tpl.name}</span>
                              <span className="text-[10px] text-slate-400">{tplCfg.label} &middot; {tpl.asset_config?.role_config || "full"}</span>
                            </div>
                            <button onClick={e => { e.stopPropagation(); handleDeleteTemplate(tpl.id); }}
                              className="p-1 text-slate-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-template-${tpl.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {getAvailableTypes("basic").map(type => {
                    const Icon = type.icon;
                    const colorMap = { lime: "lime", blue: "blue", amber: "amber", purple: "purple", teal: "teal", orange: "orange" };
                    const c = colorMap[type.color] || "lime";
                    return (
                      <button key={type.id} data-testid={`hub-type-${type.id}`} disabled={type.locked}
                        onClick={() => {
                          setSelectedAssetType(type.id);
                          const cfg = HUB_TYPES[type.id];
                          setCustomItems({ a: cfg.item_a.label, b: cfg.item_b.label, c: cfg.item_c?.label || "" });
                          setCustomUnits({ a: cfg.item_a.unit, b: cfg.item_b.unit, c: cfg.item_c?.unit || "" });
                          setItemBEnabled(cfg.item_b.enabled !== false);
                          setItemCEnabled(false);
                          if (cfg.is_task_based) setCustomActions([""]);
                          else setCustomActions([]);
                        }}
                        className={`w-full p-4 rounded-xl border-2 text-left flex items-center gap-4 transition-all ${
                          type.locked ? "opacity-40 cursor-not-allowed bg-slate-50 border-slate-200" :
                          selectedAssetType === type.id ? `bg-${c}-50 border-${c}-400 shadow-sm` : "bg-white border-slate-200 hover:border-slate-300"
                        }`}>
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                          selectedAssetType === type.id ? `bg-${c}-500 text-white` : "bg-slate-100 text-slate-400"
                        }`}><Icon className="w-5 h-5" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-900 uppercase tracking-tight">{type.label}</span>
                            {type.locked && <Lock className="w-3.5 h-3.5 text-slate-400" />}
                          </div>
                          <p className="text-xs text-slate-400 font-medium mt-0.5">{type.description}</p>
                        </div>
                        {selectedAssetType === type.id && <Check className="w-5 h-5 text-lime-500 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-3 mt-8">
                  <button type="button" onClick={() => { setView("list"); setWizardStep(1); setSelectedAssetType(""); }}
                    className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-sm font-bold">Cancel</button>
                  <button data-testid="wizard-next-btn" disabled={!selectedAssetType} onClick={() => setWizardStep(2)}
                    className="flex-[2] py-4 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-100 disabled:text-slate-400 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 shadow-md">
                    Next <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Customize Assets + Role Config */}
            {wizardStep === 2 && (() => {
              const cfg = HUB_TYPES[selectedAssetType] || HUB_TYPES.refueling;
              const TypeIcon = cfg.icon;
              return (
                <div className="animate-fadeInUp">
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-lime-50 border border-lime-300 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <TypeIcon className="w-6 h-6 text-lime-500" />
                    </div>
                    <h2 className="text-2xl font-black tracking-tighter uppercase text-slate-900 mb-1">Customize Hub</h2>
                    <p className="text-xs text-slate-400 font-medium">{cfg.label} — define your items & crew</p>
                  </div>

                  {/* Editable Items (for non-action hubs) */}
                  {!cfg.is_task_based && (
                    <div className="space-y-3 mb-6">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Asset Items</p>
                      {/* Item A — always visible */}
                      <div className="flex gap-2">
                        <input data-testid="item-a-label" value={customItems.a} onChange={e => setCustomItems(p => ({...p, a: e.target.value}))}
                          placeholder={cfg.item_a.label} className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 placeholder:text-slate-300 focus:border-lime-500 outline-none" />
                        <input data-testid="item-a-unit" value={customUnits.a} onChange={e => setCustomUnits(p => ({...p, a: e.target.value}))}
                          placeholder={cfg.item_a.unit} className="w-20 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 text-center placeholder:text-slate-300 focus:border-lime-500 outline-none" />
                      </div>
                      {/* Item B — toggleable */}
                      {itemBEnabled ? (
                        <div className="flex gap-2">
                          <input data-testid="item-b-label" value={customItems.b} onChange={e => setCustomItems(p => ({...p, b: e.target.value}))}
                            placeholder={cfg.item_b.label || "Secondary item"} className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 placeholder:text-slate-300 focus:border-lime-500 outline-none" />
                          <input data-testid="item-b-unit" value={customUnits.b} onChange={e => setCustomUnits(p => ({...p, b: e.target.value}))}
                            placeholder={cfg.item_b.unit || "unit"} className="w-20 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 text-center placeholder:text-slate-300 focus:border-lime-500 outline-none" />
                          <button onClick={() => setItemBEnabled(false)} className="p-3 text-slate-300 hover:text-red-400"><X className="w-4 h-4" /></button>
                        </div>
                      ) : !itemBEnabled && !itemCEnabled && (
                        <button onClick={() => setItemBEnabled(true)} className="flex items-center gap-2 text-xs text-lime-600 font-bold hover:text-lime-500">
                          <PlusCircle className="w-4 h-4" /> Add second item
                        </button>
                      )}
                      {/* Item C — toggleable, only if B is enabled */}
                      {itemBEnabled && cfg.max_items >= 3 && (
                        itemCEnabled ? (
                          <div className="flex gap-2">
                            <input data-testid="item-c-label" value={customItems.c} onChange={e => setCustomItems(p => ({...p, c: e.target.value}))}
                              placeholder="Third item" className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 placeholder:text-slate-300 focus:border-lime-500 outline-none" />
                            <input data-testid="item-c-unit" value={customUnits.c} onChange={e => setCustomUnits(p => ({...p, c: e.target.value}))}
                              placeholder="unit" className="w-20 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 text-center placeholder:text-slate-300 focus:border-lime-500 outline-none" />
                            <button onClick={() => { setItemCEnabled(false); setCustomItems(p => ({...p, c: ""})); }} className="p-3 text-slate-300 hover:text-red-400"><X className="w-4 h-4" /></button>
                          </div>
                        ) : (
                          <button onClick={() => setItemCEnabled(true)} className="flex items-center gap-2 text-xs text-lime-600 font-bold hover:text-lime-500">
                            <PlusCircle className="w-4 h-4" /> Add third item
                          </button>
                        )
                      )}
                    </div>
                  )}

                  {/* Action Hub — Task Editor */}
                  {cfg.is_task_based && (
                    <div className="space-y-3 mb-6">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Define Actions</p>
                      {customActions.map((action, i) => (
                        <div key={i} className="flex gap-2">
                          <input
                            data-testid={`action-input-${i}`}
                            value={action}
                            onChange={e => { const a = [...customActions]; a[i] = e.target.value; setCustomActions(a); }}
                            placeholder={["e.g. Cleaning", "e.g. Apply Emulsion Coating", "e.g. Water Plants", "e.g. Site Inspection", "e.g. Waste Removal"][i] || "New action..."}
                            className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 placeholder:text-slate-300 placeholder:font-normal focus:border-lime-500 outline-none"
                          />
                          {customActions.length > 1 && (
                            <button onClick={() => setCustomActions(customActions.filter((_, j) => j !== i))} className="p-3 text-slate-300 hover:text-red-400"><X className="w-4 h-4" /></button>
                          )}
                        </div>
                      ))}
                      <button data-testid="add-action-btn" onClick={() => setCustomActions([...customActions, ""])}
                        className="flex items-center gap-2 text-xs text-lime-600 font-bold hover:text-lime-500 py-2">
                        <PlusCircle className="w-4 h-4" /> Add action
                      </button>
                    </div>
                  )}

                  {/* Role Configuration */}
                  <div className="mb-6">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Crew Configuration</p>
                    <div className="grid grid-cols-2 gap-2">
                      {ROLE_CONFIGS.map(rc => (
                        <button key={rc.id} data-testid={`role-config-${rc.id}`} onClick={() => setSelectedRoleConfig(rc.id)}
                          className={`p-3 rounded-xl border-2 text-left transition-all ${
                            selectedRoleConfig === rc.id ? "border-lime-400 bg-lime-50" : "border-slate-200 bg-white hover:border-slate-300"
                          }`}>
                          <span className="text-xs font-bold text-slate-900 block">{rc.label}</span>
                          <span className="text-[10px] text-slate-400 block mt-0.5">Min. {rc.min_occupancy} member{rc.min_occupancy > 1 ? "s" : ""}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button type="button" onClick={() => setWizardStep(1)} className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-sm font-bold flex items-center justify-center gap-1">
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <button data-testid="wizard-step2-next" onClick={() => setWizardStep(3)}
                      className="flex-[2] py-4 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 shadow-md">
                      Next <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Step 3: Name + Deploy */}
            {wizardStep === 3 && (() => {
              const cfg = HUB_TYPES[selectedAssetType] || HUB_TYPES.refueling;
              const TypeIcon = cfg.icon;
              const roleConf = ROLE_CONFIGS.find(r => r.id === selectedRoleConfig) || ROLE_CONFIGS[0];
              return (
                <div className="animate-fadeInUp">
                  <div className="text-center mb-6">
                    <div className="w-12 h-12 bg-lime-50 border border-lime-300 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <TypeIcon className="w-6 h-6 text-lime-500" />
                    </div>
                    <h2 className="text-2xl font-black tracking-tighter uppercase text-slate-900 mb-1">Deploy Hub</h2>
                    <p className="text-xs text-slate-400 font-medium">{cfg.label} &middot; {roleConf.label}</p>
                  </div>

                  <form onSubmit={handleCreateHub} className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Hub Name</label>
                      <input data-testid="hub-name-input" value={hubName} onChange={(e) => setHubName(e.target.value)} placeholder="e.g. Stadsbader Ops" autoFocus
                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold placeholder:text-slate-400 focus:border-lime-500 focus:ring-1 focus:ring-lime-500/20 outline-none transition-all" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Description</label>
                      <input data-testid="hub-desc-input" value={hubDesc} onChange={(e) => setHubDesc(e.target.value)} placeholder="Optional description..."
                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-medium placeholder:text-slate-400 focus:border-lime-500 focus:ring-1 focus:ring-lime-500/20 outline-none transition-all" />
                    </div>

                    {/* Config Summary */}
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Configuration Summary</p>
                      {!cfg.is_task_based ? (
                        <>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="w-2 h-2 bg-lime-500 rounded-full" />
                            <span className="font-bold text-slate-800">{customItems.a || cfg.item_a.label}</span>
                            <span className="text-slate-400">({customUnits.a || cfg.item_a.unit})</span>
                          </div>
                          {itemBEnabled && (customItems.b || cfg.item_b.label) && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="w-2 h-2 bg-blue-500 rounded-full" />
                              <span className="font-bold text-slate-800">{customItems.b || cfg.item_b.label}</span>
                              <span className="text-slate-400">({customUnits.b || cfg.item_b.unit})</span>
                            </div>
                          )}
                          {itemCEnabled && customItems.c && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="w-2 h-2 bg-purple-500 rounded-full" />
                              <span className="font-bold text-slate-800">{customItems.c}</span>
                              <span className="text-slate-400">({customUnits.c})</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {customActions.filter(Boolean).map((a, i) => (
                            <span key={i} className="text-[10px] font-bold bg-orange-50 text-orange-600 border border-orange-200 px-2 py-1 rounded-full">{a}</span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs pt-2 border-t border-slate-200 mt-2">
                        <Users className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-slate-500">{roleConf.label} &middot; Min {roleConf.min_occupancy}</span>
                      </div>
                    </div>

                    {/* Save as Template */}
                    <div className="p-3 bg-amber-50/60 border border-amber-200 rounded-xl" data-testid="save-template-section">
                      <label className="flex items-center gap-3 cursor-pointer" data-testid="save-template-toggle">
                        <input type="checkbox" checked={saveAsTemplate} onChange={e => setSaveAsTemplate(e.target.checked)}
                          className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-400" />
                        <div className="flex items-center gap-1.5">
                          <Bookmark className="w-3.5 h-3.5 text-amber-500" />
                          <span className="text-xs font-bold text-slate-700">Save as template for future hubs</span>
                        </div>
                      </label>
                      {saveAsTemplate && (
                        <input data-testid="template-name-input" value={templateName} onChange={e => setTemplateName(e.target.value)}
                          placeholder="Template name, e.g. 'Cleaning Crew Setup'"
                          className="w-full mt-2 p-3 bg-white border border-amber-200 rounded-lg text-sm font-bold text-slate-900 placeholder:text-slate-400 focus:border-amber-400 outline-none" />
                      )}
                    </div>

                    <div className="flex gap-3 pt-2">
                      <button type="button" onClick={() => setWizardStep(2)} className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-sm font-bold flex items-center justify-center gap-1">
                        <ArrowLeft className="w-4 h-4" /> Back
                      </button>
                      <button data-testid="submit-create-hub-btn" type="submit" disabled={creating || !hubName.trim()}
                        className="flex-[2] py-4 bg-lime-500 hover:bg-lime-400 disabled:bg-slate-100 disabled:text-slate-400 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 shadow-md">
                        {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Deploy Hub <ArrowRight className="w-4 h-4" /></>}
                      </button>
                    </div>
                  </form>

                  <div className="mt-5 p-3 bg-slate-50/80 border border-slate-200 rounded-xl">
                    <p className="text-xs text-slate-400 font-medium leading-relaxed">
                      You will be assigned as <span className="text-lime-600 font-bold">Planner</span>.
                      {roleConf.min_occupancy > 1 && <> Invite crew to deploy. Min {roleConf.min_occupancy}/{roleConf.min_occupancy} occupancy.</>}
                      {roleConf.min_occupancy === 1 && <> Solo mode — you manage everything.</>}
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* HUB LOBBY */}
        {view === "lobby" && activeHub && (
          <div className="w-full max-w-2xl" data-testid="hub-lobby-view">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-black tracking-tighter uppercase text-slate-900 mb-1">{activeHub.name}</h2>
              <div className="flex items-center justify-center gap-2">
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${hubStatus?.is_deployed ? "bg-lime-100 text-lime-600" : "bg-amber-100 text-amber-400"}`}>
                  {hubStatus?.is_deployed ? "DEPLOYED" : "WAITING FOR CREW"}
                </span>
              </div>
            </div>

            {/* Occupancy — dynamic based on role_config */}
            {(() => {
              const rc2 = hubStatus?.role_config || "full";
              const requiredRoles = rc2 === "solo" ? ["planner"] : rc2 === "planner_driver" ? ["planner", "driver"] : rc2 === "planner_machinist" ? ["planner", "machinist"] : ["planner", "driver", "machinist"];
              const optionalRoles = ["planner", "driver", "machinist", "service", "transport", "viewer"].filter(r => !requiredRoles.includes(r));
              return (
                <>
                  <div className={`grid gap-3 mb-4 ${requiredRoles.length === 1 ? "grid-cols-1 max-w-xs mx-auto" : requiredRoles.length === 2 ? "grid-cols-2" : "grid-cols-3"}`} data-testid="occupancy-grid">
                    {requiredRoles.map((role) => {
                      const rc = ROLE_CONFIG[role];
                      const Icon = rc.icon;
                      const count = hubStatus?.occupancy?.[role] || 0;
                      const filled = count > 0;
                      return (
                        <div key={role} className={`p-5 rounded-xl border text-center transition-all ${filled ? "bg-slate-50 border-lime-400 shadow-sm" : "bg-slate-50/80 border-slate-200 border-dashed"}`} data-testid={`occupancy-${role}`}>
                          <div className={`w-12 h-12 ${filled ? rc.color : "bg-slate-100"} rounded-xl flex items-center justify-center mx-auto mb-3 ${filled ? rc.textColor : "text-slate-400"}`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <p className="text-sm font-bold text-slate-900 uppercase tracking-tight">{rc.label}</p>
                          <p className={`text-xs font-bold mt-1 ${filled ? "text-lime-600" : "text-slate-400"}`}>{filled ? `${count} online` : "Empty"}</p>
                        </div>
                      );
                    })}
                  </div>
                  {optionalRoles.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-8" data-testid="optional-roles-grid">
                      {optionalRoles.map((role) => {
                        const rc = ROLE_CONFIG[role];
                        const Icon = rc.icon;
                        const count = hubStatus?.occupancy?.[role] || 0;
                        const filled = count > 0;
                        return (
                          <div key={role} className={`p-3 rounded-xl border text-center transition-all ${filled ? "bg-slate-50 border-slate-200" : "bg-slate-50/50 border-slate-200 border-dashed"}`} data-testid={`occupancy-${role}`}>
                            <div className={`w-8 h-8 ${filled ? rc.color : "bg-slate-100"} rounded-xl flex items-center justify-center mx-auto mb-2 ${filled ? rc.textColor : "text-slate-400"}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{rc.label}</p>
                            <p className={`text-[9px] font-bold mt-0.5 ${filled ? "text-lime-600" : "text-slate-400"}`}>{filled ? `${count}` : "Optional"}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t("hub_occupancy")}</span>
                <span className="text-sm font-black text-lime-600">{hubStatus?.filled_roles || 0}/{hubStatus?.min_occupancy || 3}</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-lime-500 rounded-full transition-all duration-500"
                  style={{ width: `${((hubStatus?.filled_roles || 0) / (hubStatus?.min_occupancy || 3)) * 100}%` }}
                />
              </div>
            </div>

            {/* Members */}
            {hubStatus?.members?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">{t("active_crew")}</h3>
                <div className="grid gap-2">
                  {hubStatus.members.map((m) => {
                    const rc = ROLE_CONFIG[m.role] || ROLE_CONFIG.viewer;
                    return (
                      <div key={m.id} className="flex items-center gap-3 p-3 bg-slate-50/80 border border-slate-200 rounded-xl">
                        <div className={`w-8 h-8 ${rc.color} rounded-xl flex items-center justify-center ${rc.textColor} text-xs font-black`}>
                          {m.display_name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-bold text-slate-800">{m.display_name}</p>
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{rc.label}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Invite Section */}
            <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl mb-6" data-testid="invite-section">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">{t("invite_crew")}</h3>
              {(() => {
                const rc2 = hubStatus?.role_config || "full";
                const requiredRoles = rc2 === "solo" ? ["planner"] : rc2 === "planner_driver" ? ["planner", "driver"] : rc2 === "planner_machinist" ? ["planner", "machinist"] : ["planner", "driver", "machinist"];
                const occ = hubStatus?.occupancy || {};
                const neededRoles = requiredRoles.filter(r => r !== "planner" && !(occ[r] > 0));
                const optionalInviteRoles = ["driver", "machinist", "service", "transport", "viewer"].filter(r => !neededRoles.includes(r));
                const defaultRole = neededRoles.length > 0 ? neededRoles[0] : "driver";
                if (inviteRole === "driver" && neededRoles.length > 0 && !neededRoles.includes("driver")) {
                  // Auto-correct to needed role on first render
                  setTimeout(() => setInviteRole(defaultRole), 0);
                }
                return (
                  <>
                    {neededRoles.length > 0 && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-[11px] font-bold text-amber-600">
                          Needed to deploy: {neededRoles.map(r => ROLE_CONFIG[r]?.label || r).join(", ")}
                        </p>
                      </div>
                    )}
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {[...neededRoles, ...optionalInviteRoles].map((r) => {
                        const isNeeded = neededRoles.includes(r);
                        return (
                          <button
                            key={r}
                            data-testid={`invite-role-${r}`}
                            onClick={() => setInviteRole(r)}
                            className={`flex-1 min-w-[70px] py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-wider transition-all relative ${
                              inviteRole === r
                                ? isNeeded ? "bg-amber-100 text-amber-600 border-2 border-amber-400 shadow-sm" : "bg-lime-100 text-lime-600 border-2 border-lime-400"
                                : isNeeded ? "bg-amber-50 text-amber-500 border border-amber-300 hover:border-amber-400" : "bg-slate-100 text-slate-400 border border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            {ROLE_CONFIG[r]?.label || r}
                            {isNeeded && <span className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 bg-amber-400 rounded-full animate-pulse" />}
                          </button>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
              <div className="flex gap-2">
                <input
                  data-testid="invite-name-input"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="Display name (optional)"
                  className="flex-1 p-3 bg-white border border-slate-200 rounded-xl text-slate-900 text-sm font-medium placeholder:text-slate-400 focus:border-lime-500 outline-none"
                />
                <button
                  data-testid="create-invite-btn"
                  onClick={handleCreateInvite}
                  className="px-5 py-3 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-xs font-black uppercase"
                >
                  {t("generate_link")}
                </button>
              </div>

              {invites.length > 0 && (
                <div className="mt-4 space-y-3 max-h-[500px] overflow-y-auto">
                  {invites.map((inv) => {
                    const link = `${window.location.origin}/auth?invite=${inv.token}`;
                    const rc = ROLE_CONFIG[inv.role] || ROLE_CONFIG.viewer;
                    const isExpanded = expandedQR === inv.token;
                    return (
                      <div key={inv.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`invite-card-${inv.token}`}>
                        {/* Header row */}
                        <div className="flex items-center justify-between p-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 ${rc.color} rounded-lg flex items-center justify-center`}>
                              {(() => { const Icon = rc.icon; return <Icon className={`w-3 h-3 ${rc.textColor}`} />; })()}
                            </div>
                            <span className="text-xs font-bold text-slate-700 uppercase">{rc.label}</span>
                            {inv.display_name && <span className="text-xs text-slate-400">({inv.display_name})</span>}
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${inv.used ? "bg-slate-200 text-slate-500" : "bg-lime-100 text-lime-600"}`}>
                              {inv.used ? "USED" : "ACTIVE"}
                            </span>
                          </div>
                          {!inv.used && (
                            <button
                              onClick={() => setExpandedQR(isExpanded ? null : inv.token)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                isExpanded ? "bg-lime-100 text-lime-600 border border-lime-300" : "bg-slate-100 hover:bg-slate-200 text-slate-500 border border-transparent"
                              }`}
                              data-testid={`qr-toggle-${inv.token}`}
                            >
                              <QrCode className="w-3.5 h-3.5" />
                              {isExpanded ? "Hide QR" : "QR Code"}
                            </button>
                          )}
                        </div>

                        {/* QR Code expanded view */}
                        {!inv.used && isExpanded && (
                          <div className="px-3 pb-4 animate-fadeIn">
                            <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col items-center" data-testid={`qr-panel-${inv.token}`}>
                              <QRCodeSVG
                                id={`qr-svg-${inv.token}`}
                                value={link}
                                size={200}
                                level="M"
                                includeMargin={false}
                                bgColor="#ffffff"
                                fgColor="#0f172a"
                              />
                              <p className="text-xs font-bold text-slate-700 mt-3 text-center">{activeHub?.name}</p>
                              <p className="text-[10px] text-slate-400 text-center">Join as {rc.label}</p>
                            </div>

                            {/* Action buttons */}
                            <div className="grid grid-cols-3 gap-2 mt-3">
                              <button
                                onClick={() => shareInvite(inv.token)}
                                className="flex items-center justify-center gap-1.5 py-2.5 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-xs font-black uppercase active:scale-[0.97] transition-all"
                                data-testid={`share-invite-${inv.token}`}
                              >
                                <Share2 className="w-3.5 h-3.5" /> Share
                              </button>
                              <button
                                onClick={() => downloadQR(inv.token)}
                                className="flex items-center justify-center gap-1.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold uppercase border border-slate-200 active:scale-[0.97] transition-all"
                                data-testid={`download-qr-${inv.token}`}
                              >
                                <Download className="w-3.5 h-3.5" /> Save
                              </button>
                              <button
                                onClick={() => copyInviteLink(inv.token)}
                                className="flex items-center justify-center gap-1.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold uppercase border border-slate-200 active:scale-[0.97] transition-all"
                                data-testid={`copy-invite-${inv.token}`}
                              >
                                {copied === inv.token ? <Check className="w-3.5 h-3.5 text-lime-600" /> : <Copy className="w-3.5 h-3.5" />}
                                {copied === inv.token ? "Copied" : "Copy"}
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Collapsed link row */}
                        {!inv.used && !isExpanded && (
                          <div className="px-3 pb-3 flex gap-2">
                            <input id={`invite-link-${inv.token}`} readOnly value={link}
                              onClick={e => { e.target.select(); copyInviteLink(inv.token); }}
                              className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[11px] font-mono text-blue-600 cursor-pointer select-all focus:border-blue-400 outline-none"
                              data-testid={`invite-link-${inv.token}`} />
                            <button
                              onClick={() => shareInvite(inv.token)}
                              className="px-3 py-2 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-lg text-xs font-bold flex items-center gap-1"
                              data-testid={`share-quick-${inv.token}`}
                            >
                              <Share2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Enter / Deploy button */}
            {hubStatus?.is_deployed ? (
              <button
                data-testid="enter-hub-btn"
                onClick={() => {
                  const myMembership = myHubs.find(h => h.hub_id === activeHub.id) || hubStatus?.members?.[0];
                  if (myMembership) handleEnterHub({ ...myMembership, hub_name: activeHub.name });
                }}
                className="w-full py-5 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 shadow-md"
              >
                {t("enter_hub")} <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <div className="text-center p-4 bg-slate-50/80 border border-amber-300 rounded-xl">
                <Signal className="w-6 h-6 text-amber-400 mx-auto mb-2 animate-pulse" />
                <p className="text-sm text-amber-400 font-bold">Waiting for minimum crew ({hubStatus?.filled_roles || 0}/{hubStatus?.min_occupancy || 3})</p>
                <p className="text-xs text-slate-400 mt-1">
                  {(() => {
                    const rc2 = hubStatus?.role_config || "full";
                    if (rc2 === "solo") return "Hub deploys with just the Planner";
                    if (rc2 === "planner_driver") return "Hub deploys when Planner + Driver are present";
                    if (rc2 === "planner_machinist") return "Hub deploys when Planner + Machinist are present";
                    return "Hub deploys when Planner + Driver + Machinist are present";
                  })()}
                </p>
                <button
                  onClick={() => loadHubStatus(activeHub.id)}
                  className="mt-3 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-xs font-bold flex items-center gap-2 mx-auto"
                  data-testid="refresh-status-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh Status
                </button>
              </div>
            )}
          </div>
        )}

        {/* JOIN PREVIEW */}
        {view === "join-preview" && (
          <div className="w-full max-w-md" data-testid="join-preview-view">
            {!joinInfo ? (
              <div className="text-center">
                <Loader2 className="w-10 h-10 text-lime-500 animate-spin mx-auto mb-4" />
                <p className="text-slate-400 font-medium">Loading invite...</p>
              </div>
            ) : joinInfo.used ? (
              <div className="text-center animate-fadeInUp">
                <div className="w-16 h-16 bg-slate-100 border border-slate-200 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <X className="w-8 h-8 text-slate-400" />
                </div>
                <h2 className="text-2xl font-black tracking-tighter uppercase text-slate-900 mb-2">{t("invite_used")}</h2>
                <p className="text-sm text-slate-400 font-medium mb-6">{t("invite_used_desc")}</p>
                <button onClick={() => { setView("list"); loadMyHubs(); nav("/portal"); }}
                  className="px-6 py-3 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-sm font-black uppercase" data-testid="go-to-portal-btn">
                  {t("go_to_hubs")}
                </button>
              </div>
            ) : (
              <div className="animate-fadeInUp">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-lime-50 border border-lime-300 rounded-xl flex items-center justify-center mx-auto mb-5">
                    <Users className="w-8 h-8 text-lime-500" />
                  </div>
                  <h2 className="text-3xl font-black tracking-tighter uppercase text-slate-900 mb-2">{t("youre_invited")}</h2>
                  <p className="text-sm text-slate-400 font-medium">{t("join_the_crew")}</p>
                </div>

                {/* Hub Info Card */}
                <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl mb-5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">{t("hub_details")}</p>
                  <h3 className="text-xl font-black text-slate-900 tracking-tight mb-2" data-testid="join-hub-name">{joinInfo.hub_name}</h3>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-[10px] font-bold px-2 py-1 bg-slate-100 text-slate-500 rounded-full uppercase">{joinInfo.hub_type}</span>
                    <span className="text-[10px] font-bold px-2 py-1 bg-slate-100 text-slate-500 rounded-full">{joinInfo.current_members} member{joinInfo.current_members !== 1 ? "s" : ""}</span>
                  </div>
                </div>

                {/* Role Badge — BIG */}
                {(() => {
                  const rc = ROLE_CONFIG[joinInfo.role] || ROLE_CONFIG.viewer;
                  const Icon = rc.icon;
                  return (
                    <div className={`p-6 ${rc.color} rounded-xl mb-5 flex items-center gap-4`} data-testid="join-role-badge">
                      <div className={`w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center ${rc.textColor}`}>
                        <Icon className="w-7 h-7" strokeWidth={2} />
                      </div>
                      <div>
                        <p className={`text-xs font-bold uppercase tracking-widest ${rc.textColor} opacity-80`}>{t("your_role")}</p>
                        <p className={`text-2xl font-black uppercase tracking-tight ${rc.textColor}`}>{rc.label}</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Display Name Input */}
                <div className="mb-5">
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{t("display_name")}</label>
                  <input
                    data-testid="join-display-name-input"
                    value={joinDisplayName}
                    onChange={(e) => setJoinDisplayName(e.target.value)}
                    placeholder={t("how_crew_sees_you")}
                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold placeholder:text-slate-400 focus:border-lime-500 focus:ring-1 focus:ring-lime-500/20 outline-none transition-all"
                  />
                </div>

                {/* Join Button */}
                <button
                  data-testid="confirm-join-btn"
                  onClick={handleConfirmJoin}
                  disabled={joinLoading}
                  className={`w-full py-5 ${ROLE_CONFIG[joinInfo.role]?.color || "bg-lime-500"} hover:opacity-90 ${ROLE_CONFIG[joinInfo.role]?.textColor || "text-slate-950"} rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 shadow-lg transition-all active:scale-[0.98]`}
                >
                  {joinLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {t("join_as")} {ROLE_CONFIG[joinInfo.role]?.label || "Member"} <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                <p className="text-center text-[11px] text-slate-400 mt-4 font-medium">
                  You'll be added to <span className="font-bold text-slate-500">{joinInfo.hub_name}</span> as a <span className="font-bold text-slate-500">{ROLE_CONFIG[joinInfo.role]?.label}</span>.
                </p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
